export interface SeoContentSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
  code?: string;
}

export interface SeoContentPage {
  path: string;
  kind: "guide" | "docs" | "tool";
  title: string;
  description: string;
  eyebrow: string;
  heading: string;
  intro: string;
  sections: SeoContentSection[];
  relatedPaths: string[];
}

export const SEO_CONTENT_PAGES: readonly SeoContentPage[] = [
  {
    path: "/create-codex-pet",
    kind: "guide",
    title: "How to Create a Custom Codex Pet | Codex Pet Lab",
    description: "Create a custom Codex pet with the correct pet.json manifest, V2 spritesheet dimensions, 11 animation rows, and 16 look directions.",
    eyebrow: "Creator guide",
    heading: "How to Create a Custom Codex Pet",
    intro: "A Codex pet is a small animated companion packaged as pet.json plus spritesheet.webp. Codex Pet Lab validates, previews, packages, and installs pets that follow the V2 animation contract.",
    sections: [
      {
        heading: "1. Start with a pet concept",
        paragraphs: ["Choose one readable full-body character with a stable face, palette, silhouette, and props. Every frame must remain clear inside a 192×208 cell, so avoid tiny text, detached effects, soft shadows, and scenery."],
        bullets: ["Give the pet a lowercase slug such as cloud-bun.", "Write a short display name and one-sentence description.", "Decide whether the character is an animal, person, robot, or another compact mascot."],
      },
      {
        heading: "2. Produce the V2 animation atlas",
        paragraphs: ["A new V2 pet uses an 8-column by 11-row spritesheet. Rows 0–8 contain standard Codex states. Rows 9–10 contain 16 clockwise look directions."],
        bullets: ["Cell size: 192×208 pixels", "Atlas size: 1536×2288 pixels", "Format: transparent WebP or PNG", "Version: spriteVersionNumber 2"],
      },
      {
        heading: "3. Create pet.json",
        code: `{
  "id": "cloud-bun",
  "displayName": "Cloud Bun",
  "description": "A soft deployment bunny for Codex.",
  "spritesheetPath": "spritesheet.webp",
  "spriteVersionNumber": 2,
  "kind": "animal"
}`,
      },
      {
        heading: "4. Validate and preview locally",
        paragraphs: ["Open My Library in Codex Pet Lab and select pet.json and spritesheet.webp. Validation runs locally in the browser, confirms the manifest and atlas dimensions, and plays frames from the real spritesheet before anything is uploaded."],
      },
      {
        heading: "5. Package and install",
        paragraphs: ["Download a .codex-pet.zip package or install a published pet with the codexpetlab CLI."],
        code: "npx codexpetlab add cloud-bun",
      },
    ],
    relatedPaths: ["/docs/v2-spritesheet", "/docs/pet-json", "/tools/codex-pet-validator", "/install-codex-pet"],
  },
  {
    path: "/install-codex-pet",
    kind: "guide",
    title: "Install Codex Pets with npx or ZIP | Codex Pet Lab",
    description: "Install one Codex pet or an entire collection with npx codexpetlab, or manually install pet.json and spritesheet.webp into CODEX_HOME.",
    eyebrow: "Installation guide",
    heading: "Install a Codex Pet",
    intro: "Codex Pet Lab publishes a first-party npm installer and standard ZIP packages. Both methods write the same two files into your Codex pets directory.",
    sections: [
      { heading: "Install one pet", code: "npx codexpetlab add miso-byte" },
      { heading: "Install a collection", code: "npx codexpetlab add-collection terminal-companions" },
      {
        heading: "Installation location",
        paragraphs: ["By default pets are installed below $HOME/.codex/pets. Set CODEX_HOME when Codex uses a different data directory."],
        code: `CODEX_HOME=/custom/codex npx codexpetlab add miso-byte`,
      },
      {
        heading: "Manual installation",
        paragraphs: ["Extract the package into a directory whose name matches the manifest id."],
        code: `$HOME/.codex/pets/miso-byte/
├── pet.json
└── spritesheet.webp`,
      },
      {
        heading: "Validate before installation",
        code: "npx codexpetlab validate ./miso-byte.codex-pet.zip",
      },
    ],
    relatedPaths: ["/pets", "/create-codex-pet", "/tools/codex-pet-validator", "/docs/pet-json"],
  },
  {
    path: "/tools/codex-pet-validator",
    kind: "tool",
    title: "Free Codex Pet Validator | Codex Pet Lab",
    description: "Validate pet.json and spritesheet.webp locally in your browser. Check slug, required fields, spriteVersionNumber, WebP format, and V2 atlas dimensions.",
    eyebrow: "Browser tool",
    heading: "Free Codex Pet Validator",
    intro: "The Codex Pet Lab validator reads files locally. It does not upload your pet unless you explicitly submit it for community review.",
    sections: [
      {
        heading: "What structural validation checks",
        bullets: ["pet.json is valid JSON", "id is a lowercase slug", "displayName and description are present", "spritesheetPath is spritesheet.webp", "spriteVersionNumber is supported", "WebP dimensions match the selected version"],
      },
      {
        heading: "V2 requirements",
        bullets: ["1536×2288 atlas", "8 columns and 11 rows", "192×208 cells", "16 look-direction cells in rows 9–10"],
      },
      {
        heading: "What requires visual QA",
        paragraphs: ["Structural validation cannot prove that animations are smooth, backgrounds are fully transparent, direction semantics are correct, or the character identity remains stable. Use contact sheets and motion previews for those checks."],
      },
      {
        heading: "Validate from the command line",
        code: "npx codexpetlab validate ./my-pet.codex-pet.zip",
      },
    ],
    relatedPaths: ["/library", "/create-codex-pet", "/docs/v2-spritesheet", "/docs/pet-json"],
  },
  {
    path: "/docs/v2-spritesheet",
    kind: "docs",
    title: "Codex Pet V2 Spritesheet Format — 1536×2288 | Codex Pet Lab",
    description: "Reference for the Codex Pet V2 8×11 spritesheet: 192×208 cells, nine animation states, and 16 clockwise look directions.",
    eyebrow: "Format reference",
    heading: "Codex Pet V2 Spritesheet Format",
    intro: "V2 extends the original nine-row Codex pet atlas with two complete look-direction rows. The final WebP is exactly 1536×2288 pixels.",
    sections: [
      {
        heading: "Atlas geometry",
        bullets: ["Columns: 8", "Rows: 11", "Cell width: 192 pixels", "Cell height: 208 pixels", "Final size: 1536×2288 pixels"],
      },
      {
        heading: "Rows 0–8: standard states",
        bullets: ["0 idle", "1 running-right", "2 running-left", "3 waving", "4 jumping", "5 failed", "6 waiting", "7 running or active work", "8 review"],
      },
      {
        heading: "Rows 9–10: look directions",
        paragraphs: ["The 16 cells form one clockwise sequence in 22.5-degree steps. 000 means up, 090 means screen-right, 180 means down, and 270 means screen-left."],
        code: `row 9:  000, 022.5, 045, 067.5, 090, 112.5, 135, 157.5
row 10: 180, 202.5, 225, 247.5, 270, 292.5, 315, 337.5`,
      },
      {
        heading: "Transparency and unused cells",
        paragraphs: ["Unused cells in standard animation rows should remain fully transparent. Avoid backgrounds, cast shadows, labels, guide marks, detached effects, and content crossing into neighboring cells."],
      },
    ],
    relatedPaths: ["/create-codex-pet", "/docs/pet-json", "/tools/codex-pet-validator", "/pets"],
  },
  {
    path: "/docs/pet-json",
    kind: "docs",
    title: "Codex Pet pet.json Manifest Reference | Codex Pet Lab",
    description: "Learn the required pet.json fields for a Codex pet package, including id, displayName, description, spritesheetPath, and spriteVersionNumber.",
    eyebrow: "Manifest reference",
    heading: "Codex Pet pet.json Reference",
    intro: "The manifest identifies the pet and tells Codex which spritesheet contract to use. Keep pet.json and spritesheet.webp together in the same pet directory or ZIP root.",
    sections: [
      {
        heading: "V2 example",
        code: `{
  "id": "miso-byte",
  "displayName": "Miso Byte",
  "description": "A tiny debugging cat for Codex.",
  "spritesheetPath": "spritesheet.webp",
  "spriteVersionNumber": 2,
  "kind": "animal"
}`,
      },
      {
        heading: "Field rules",
        bullets: ["id: lowercase letters, numbers, and hyphens", "displayName: human-readable pet name", "description: short explanation shown in galleries", "spritesheetPath: spritesheet.webp", "spriteVersionNumber: use 2 for an 8×11 atlas", "kind: optional category such as animal or person"],
      },
      {
        heading: "Common errors",
        bullets: ["Manifest id does not match the installation directory", "spriteVersionNumber is missing from a V2 atlas", "spritesheetPath points to another filename", "WebP dimensions do not match the selected version", "Files are nested inside an extra ZIP directory"],
      },
    ],
    relatedPaths: ["/docs/v2-spritesheet", "/create-codex-pet", "/install-codex-pet", "/tools/codex-pet-validator"],
  },
  {
    path: "/tools/spritesheet-player",
    kind: "tool",
    title: "Online Codex Pet Spritesheet Player | Codex Pet Lab",
    description: "Play real Codex pet animation frames from a V2 spritesheet. Preview idle, running, waving, jumping, failed, waiting, and review states online.",
    eyebrow: "Interactive tool",
    heading: "Codex Pet Spritesheet Player",
    intro: "The player crops real 192×208 cells from a Codex pet atlas and uses the state-specific frame timing defined by the V2 contract.",
    sections: [
      { heading: "Preview real frames", paragraphs: ["This is not a CSS approximation. Each frame is read from the selected row and column of spritesheet.webp and drawn to a pixel-preserving canvas."] },
      { heading: "Supported states", bullets: ["idle", "running-right", "running-left", "waving", "jumping", "failed", "waiting", "running", "review"] },
      { heading: "Use the npm player", code: `import { SpritePlayer } from "codexpetlab-player";

<SpritePlayer
  src="/spritesheet.webp"
  state="idle"
  width={384}
/>` },
    ],
    relatedPaths: ["/tools/gif-exporter", "/tools/direction-playground", "/docs/v2-spritesheet", "/pets/miso-byte"],
  },
  {
    path: "/tools/gif-exporter",
    kind: "tool",
    title: "Codex Pet GIF Exporter | Codex Pet Lab",
    description: "Export a Codex pet animation state as a transparent GIF directly from its real spritesheet frames.",
    eyebrow: "Interactive tool",
    heading: "Codex Pet GIF Exporter",
    intro: "Choose an animation state and export the real frames as a shareable GIF. Encoding happens locally in the browser.",
    sections: [
      { heading: "How GIF export works", bullets: ["Loads spritesheet.webp", "Crops the used cells for the selected state", "Preserves the documented per-frame delays", "Quantizes transparent RGBA frames", "Downloads a GIF89a file"] },
      { heading: "Best uses", paragraphs: ["Use exported GIFs in documentation, social posts, pull requests, creator profiles, and visual QA. The original WebP atlas remains the installable source of truth."] },
    ],
    relatedPaths: ["/tools/spritesheet-player", "/tools/direction-playground", "/docs/v2-spritesheet", "/create-codex-pet"],
  },
  {
    path: "/tools/direction-playground",
    kind: "tool",
    title: "Codex Pet 16 Direction Playground | Codex Pet Lab",
    description: "Test all 16 clockwise look directions in a Codex Pet V2 spritesheet by moving your pointer around the pet.",
    eyebrow: "Interactive tool",
    heading: "Codex Pet 16 Direction Playground",
    intro: "Move the pointer around the sample pet to select the nearest 22.5-degree direction cell. The center deadzone falls back to the idle animation.",
    sections: [
      { heading: "Direction order", code: `000 up → 090 right → 180 down → 270 left → 337.5 up-left → 000 up` },
      { heading: "What to inspect", bullets: ["Cardinal directions are unmistakable", "Adjacent directions move continuously", "Scale and baseline stay stable", "Eyes, face, body, ears, and props follow natural mechanics", "The loop does not reverse or snap"] },
    ],
    relatedPaths: ["/docs/v2-spritesheet", "/tools/spritesheet-player", "/tools/gif-exporter", "/create-codex-pet"],
  },
  {
    path: "/docs/v1-vs-v2",
    kind: "docs",
    title: "Codex Pet V1 vs V2 Spritesheets | Codex Pet Lab",
    description: "Compare Codex Pet V1 and V2 atlas sizes, animation rows, spriteVersionNumber behavior, and 16-direction support.",
    eyebrow: "Format comparison",
    heading: "Codex Pet V1 vs V2",
    intro: "V1 uses the original nine animation rows. V2 adds two direction rows while preserving the standard states.",
    sections: [
      { heading: "V1", bullets: ["1536×1872 pixels", "8 columns × 9 rows", "192×208 cells", "Nine standard animation states", "spriteVersionNumber omitted or set to 1"] },
      { heading: "V2", bullets: ["1536×2288 pixels", "8 columns × 11 rows", "Nine standard animation states", "16 clockwise look directions", "spriteVersionNumber set to 2"] },
      { heading: "Upgrade rule", paragraphs: ["Keep approved standard rows, add two coherent look-direction rows, validate transparency and direction semantics, then update the manifest to spriteVersionNumber 2. Do not label a 9-row atlas as V2."] },
    ],
    relatedPaths: ["/docs/v2-spritesheet", "/docs/pet-json", "/create-codex-pet", "/tools/direction-playground"],
  },
  {
    path: "/guides/troubleshooting",
    kind: "guide",
    title: "Codex Pet Troubleshooting Guide | Codex Pet Lab",
    description: "Fix common Codex pet installation, manifest, spritesheet dimension, transparency, animation, and direction problems.",
    eyebrow: "Troubleshooting guide",
    heading: "Codex Pet Troubleshooting",
    intro: "Most pet failures come from an incorrect install path, a manifest mismatch, wrong atlas dimensions, opaque backgrounds, or frames in the wrong row.",
    sections: [
      { heading: "Pet does not appear", bullets: ["Confirm the directory is inside $HOME/.codex/pets or CODEX_HOME/pets", "Confirm pet.json and spritesheet.webp are at the directory root", "Confirm the directory and manifest id match", "Restart or refresh Codex after installation"] },
      { heading: "Spritesheet rejected", bullets: ["V1 must be 1536×1872", "V2 must be 1536×2288", "spriteVersionNumber must match the atlas", "spritesheetPath must be spritesheet.webp"] },
      { heading: "Animation is wrong", bullets: ["Check row order", "Keep unused cells transparent", "Preserve frame order when mirroring running-left", "Inspect contact sheets and GIF previews for size or baseline popping"] },
      { heading: "Look directions are wrong", bullets: ["000 is up, not neutral", "090 is screen-right", "180 is down", "270 is screen-left", "The full sequence must advance clockwise without reversals"] },
      { heading: "Run structural validation", code: "npx codexpetlab validate ./my-pet.codex-pet.zip" },
    ],
    relatedPaths: ["/install-codex-pet", "/tools/codex-pet-validator", "/docs/pet-json", "/docs/v2-spritesheet"],
  },
];

export function getSeoContentPage(pathname: string): SeoContentPage | undefined {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return SEO_CONTENT_PAGES.find((page) => page.path === normalized);
}
